import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { type AddPreview, type LibrarySearch, type MbResult, endpoints } from "../api.js";
import { Search } from "./Search.js";

/**
 * Search, wired.
 *
 * The two halves are fetched on different clocks and that is the point. The library answers from
 * Postgres in a millisecond, so it runs almost immediately; MusicBrainz costs one of exactly one
 * request per second, so it waits for a pause in the typing.
 *
 * The server is already safe without the debounce — a serial limiter and a query cache see to
 * that — but "safe" would mean a typist queueing eight requests and reading the answer to their
 * fourth keystroke eight seconds later. The debounce is about the ANSWER arriving while it is
 * still wanted, not about protecting MusicBrainz.
 */

const LIBRARY_DEBOUNCE_MS = 90;
const MB_DEBOUNCE_MS = 400;

export function SearchScreen({ q, onQuery }: { q: string; onQuery: (q: string) => void }) {
  const navigate = useNavigate();

  const [library, setLibrary] = useState<LibrarySearch | null>(null);
  const [mb, setMb] = useState<MbResult[] | null>(null);
  const [mbLoading, setMbLoading] = useState(false);
  const [mbError, setMbError] = useState<string | null>(null);
  const [mbNonce, setMbNonce] = useState(0);

  const [preview, setPreview] = useState<AddPreview | null>(null);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  /** The result the panel is about, kept so a release re-pick can ask for the preview again. */
  const chosen = useRef<MbResult | null>(null);

  const term = q.trim();

  useEffect(() => {
    if (term === "") {
      setLibrary(null);
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      void endpoints.search(term).then((result) => {
        // A response for a query the user has already typed past must not paint over the new one.
        if (live) setLibrary(result);
      });
    }, LIBRARY_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [term]);

  useEffect(() => {
    if (term === "") {
      setMb(null);
      setMbError(null);
      return;
    }
    let live = true;
    setMbLoading(true);
    setMbError(null);
    const timer = setTimeout(() => {
      void endpoints
        .searchMusicBrainz(term)
        .then((result) => {
          if (live) setMb(result.results);
        })
        .catch(() => {
          // Distinct from an empty list on purpose: "unreachable" and "does not exist" are
          // opposite statements, and the screen says which one this is.
          if (live) setMbError("unavailable");
        })
        .finally(() => {
          if (live) setMbLoading(false);
        });
    }, MB_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [term, mbNonce]);

  const loadPreview = useCallback(async (result: MbResult, releaseMbid?: string) => {
    chosen.current = result;
    setPreviewFor(result.mbid);
    setPreviewLoading(true);
    try {
      setPreview(
        await endpoints.addPreview({
          releaseGroupMbid: result.mbid,
          title: result.title,
          artist: result.artist,
          year: result.firstReleaseDate ? Number(result.firstReleaseDate.slice(0, 4)) : null,
          releaseMbid,
        }),
      );
    } catch {
      setPreview(null);
      setMbError("unavailable");
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const onAdd = useCallback(
    async (bonus: number[], startRating: boolean) => {
      if (!preview || !chosen.current) return;
      setAdding(true);
      try {
        const result = await endpoints.add({
          releaseGroupMbid: preview.releaseGroupMbid,
          releaseMbid: preview.chosenReleaseMbid,
          title: preview.title,
          artist: preview.artist,
          year: chosen.current.firstReleaseDate ? Number(chosen.current.firstReleaseDate.slice(0, 4)) : null,
          primaryType: chosen.current.primaryType,
          bonus,
        });
        setPreview(null);
        // Desktop starts rating; "Add only" and mobile go to the record instead (§8).
        await navigate(startRating ? { to: "/rate" } : { to: "/album/$albumId", params: { albumId: String(result.albumId) } });
      } finally {
        setAdding(false);
      }
    },
    [preview, navigate],
  );

  return (
    <Search
      q={q}
      onQuery={onQuery}
      library={library}
      mb={mb}
      mbLoading={mbLoading}
      mbError={mbError}
      onRetryMb={() => setMbNonce((n) => n + 1)}
      preview={preview}
      previewFor={previewFor}
      previewLoading={previewLoading}
      onPreview={(result) => void loadPreview(result)}
      onClosePreview={() => {
        setPreview(null);
        setPreviewFor(null);
      }}
      onChooseRelease={(releaseMbid) => {
        if (chosen.current) void loadPreview(chosen.current, releaseMbid);
      }}
      onAdd={(bonus, startRating) => void onAdd(bonus, startRating)}
      adding={adding}
    />
  );
}
